import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { t, font, Button } from "@realtyflows/ui";
import { getEsignQuota } from "../api";

/**
 * EsignQuotaBanner — global warning banner for e-sign quota.
 *
 * Shown app-wide (mounted once at the App layout level) when:
 *  • Warning level (≥ 80% used) — gentle nudge to buy add-on or upgrade
 *  • Exhausted level (no remaining) — hard reminder
 *
 * Hidden:
 *  • On the /billing page itself (avoid double messaging)
 *  • When user dismisses for the session (sessionStorage)
 *  • When quota is healthy
 */
export default function EsignQuotaBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [quota, setQuota] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("esign_quota_banner_dismissed") === "1");

  useEffect(() => {
    // Re-fetch on route change so the banner stays accurate
    getEsignQuota().then(setQuota).catch(() => setQuota(null));
  }, [location.pathname]);

  const handleDismiss = () => {
    sessionStorage.setItem("esign_quota_banner_dismissed", "1");
    setDismissed(true);
  };

  // Hide on billing page (the meter there shows the same info)
  if (location.pathname.startsWith("/billing")) return null;
  if (dismissed) return null;
  if (!quota) return null;
  if (quota.warningLevel === "ok") return null;

  const isExhausted = quota.warningLevel === "exhausted";
  const color = isExhausted ? t.coral : t.amber;

  return (
    <div style={{
      background: color + "12",
      border: `1px solid ${color}40`,
      borderRadius: 10,
      padding: "10px 14px",
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 12,
      fontFamily: font.body,
    }}>
      <div style={{ width: 8, height: 8, borderRadius: 99, background: color }} />
      <div style={{ flex: 1, fontSize: 12, color: t.textPrimary }}>
        {isExhausted ? (
          <><strong>You're out of e-sign envelopes this month.</strong> Buy an add-on pack or upgrade your plan to keep sending folders for signing.</>
        ) : (
          <>You've used <strong>{quota.percentUsed}%</strong> of your monthly envelopes ({quota.used}/{quota.limit}). One envelope = one folder send. Buy an add-on or upgrade to avoid interruption.</>
        )}
      </div>
      <Button variant={isExhausted ? "primary" : "secondary"} onClick={() => navigate("/billing")}>
        {isExhausted ? "Buy now" : "Manage"}
      </Button>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          fontSize: 16, color: t.textMuted, padding: "0 4px", lineHeight: 1, fontWeight: 700,
        }}
      >&times;</button>
    </div>
  );
}
